/**
 * Image Hotspot Element Editor
 * Background image with draggable, resizable hotspots
 */

import * as React from 'react';
import {
    Button,
    Input,
    Label,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    cn,
} from '@ai-content-hub/content-kit';
import { Plus, Trash2, ImagePlus, ImageMinus, GripHorizontal } from 'lucide-react';
import {
    ImageDropCanvas,
    useCanvasSize,
    getCanvasScale,
} from '@ai-content-hub/content-kit';
import type { BlockContent } from '../block';
import { BlockEditor, BlockRenderer, createDefaultBlockContent } from '../block';
import {
    type ImageHotspotElementData,
    type ImageHotspotItem,
    normalizeImageHotspotData,
    createDefaultHotspot,
    getBackgroundImageSrc,
} from './schema';
import { useElementUITranslations } from '../i18n/elementUI';

const MIN_HOTSPOTS = 0;
const MAX_HOTSPOTS = 20;
const RADIUS_MIN = 20;
const RADIUS_MAX = 150;
const EDITOR_DRAGGABLE_PREFIX = 'editor-hotspot-';

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

function DraggableHotspotNode({
    hotspot,
    updateHotspot,
    disabled,
    isSelected,
    onSelect,
    containerRef,
}: {
    hotspot: ImageHotspotItem;
    updateHotspot: (id: string, updates: Partial<ImageHotspotItem>) => void;
    disabled: boolean;
    isSelected: boolean;
    onSelect: (id: string) => void;
    containerRef: React.RefObject<HTMLDivElement | null>;
}) {
    const et = useElementUITranslations();
    const [transform, setTransform] = React.useState<{ x: number; y: number } | null>(null);
    const canvasSize = useCanvasSize();
    const radius = clamp(hotspot.radius, RADIUS_MIN, RADIUS_MAX);
    const baseDiameter = radius * 2;
    const size = baseDiameter * getCanvasScale(canvasSize);

    const style: React.CSSProperties = {
        position: 'absolute',
        left: `${hotspot.x}%`,
        top: `${hotspot.y}%`,
        transform: transform
            ? `translate(${transform.x}px, ${transform.y}px) translate(-50%, -50%)`
            : 'translate(-50%, -50%)',
        width: size,
        height: size,
        cursor: disabled ? 'default' : 'grab',
        zIndex: isSelected ? 10 : 1,
    };

    const handlePointerDown = React.useCallback(
        (e: React.PointerEvent) => {
            if (disabled || !containerRef.current) return;
            e.stopPropagation();
            const startX = e.clientX;
            const startY = e.clientY;
            let currentX = 0;
            let currentY = 0;

            const handleMove = (moveEvent: PointerEvent) => {
                currentX = moveEvent.clientX - startX;
                currentY = moveEvent.clientY - startY;
                setTransform({ x: currentX, y: currentY });
            };

            const handleUp = () => {
                window.removeEventListener('pointermove', handleMove);
                window.removeEventListener('pointerup', handleUp);
                if (containerRef.current && canvasSize) {
                    const deltaXPercent = (currentX / canvasSize.width) * 100;
                    const deltaYPercent = (currentY / canvasSize.height) * 100;
                    updateHotspot(hotspot.id, {
                        x: clamp(hotspot.x + deltaXPercent, 0, 100),
                        y: clamp(hotspot.y + deltaYPercent, 0, 100),
                    });
                }
                setTransform(null);
            };

            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            window.addEventListener('pointermove', handleMove);
            window.addEventListener('pointerup', handleUp);
        },
        [containerRef, canvasSize, disabled, hotspot, updateHotspot]
    );

    const handleResizePointerDown = React.useCallback(
        (e: React.PointerEvent) => {
            e.stopPropagation();
            if (disabled || !containerRef.current) return;

            const handleMove = (moveEvent: PointerEvent) => {
                const rect = containerRef.current?.getBoundingClientRect();
                if (!rect) return;
                const imgW = canvasSize?.width ?? rect.width;
                const imgH = canvasSize?.height ?? rect.height;
                const imgLeft = rect.left + (canvasSize?.left ?? 0);
                const imgTop = rect.top + (canvasSize?.top ?? 0);
                const centerX = imgLeft + (hotspot.x / 100) * imgW;
                const centerY = imgTop + (hotspot.y / 100) * imgH;
                const dx = moveEvent.clientX - centerX;
                const dy = moveEvent.clientY - centerY;
                const distPx = Math.sqrt(dx * dx + dy * dy);
                const scale = getCanvasScale(canvasSize);
                const newRadius =
                    scale > 0
                        ? clamp(
                            Math.round(distPx / scale),
                            RADIUS_MIN,
                            RADIUS_MAX
                        )
                        : hotspot.radius;
                if (newRadius !== hotspot.radius) {
                    updateHotspot(hotspot.id, { radius: newRadius });
                }
            };

            const handleUp = () => {
                window.removeEventListener('pointermove', handleMove);
                window.removeEventListener('pointerup', handleUp);
                (e.target as HTMLElement).releasePointerCapture(e.pointerId);
            };

            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            window.addEventListener('pointermove', handleMove);
            window.addEventListener('pointerup', handleUp);
        },
        [containerRef, canvasSize, disabled, hotspot, updateHotspot]
    );

    const handleClick = React.useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (!disabled) onSelect(hotspot.id);
        },
        [disabled, hotspot.id, onSelect]
    );

    return (
        <div
            style={style}
            onPointerDown={handlePointerDown}
            onClick={handleClick}
            className={cn(
                'relative flex items-center justify-center rounded-full border-2 overflow-hidden',
                'border-primary bg-primary/20',
                isSelected && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
                !disabled && 'active:cursor-grabbing'
            )}
        >
            <span className="text-xs font-medium truncate px-2 text-primary-foreground">
                {hotspot.label || hotspot.id}
            </span>
            {!disabled && (
                <div
                    role="button"
                    tabIndex={0}
                    onPointerDown={handleResizePointerDown}
                    className="absolute bottom-0 right-0 w-5 h-5 flex items-center justify-center bg-primary/80 text-primary-foreground rounded-tl cursor-se-resize hover:bg-primary"
                    aria-label={et.resize}
                >
                    <GripHorizontal className="h-3 w-3" />
                </div>
            )}
        </div>
    );
}

export interface ImageHotspotEditProps {
    value: ImageHotspotElementData;
    onChange: (value: ImageHotspotElementData) => void;
    disabled?: boolean;
    className?: string;
}

export const ImageHotspotEdit: React.FC<ImageHotspotEditProps> = ({
    value,
    onChange,
    disabled = false,
    className,
}) => {
    const et = useElementUITranslations();
    const data = React.useMemo(
        () => normalizeImageHotspotData(value ?? {}),
        [value]
    );

    const updateData = (updates: Partial<ImageHotspotElementData>) => {
        onChange(normalizeImageHotspotData({ ...data, ...updates }));
    };

    const updateHotspot = (id: string, updates: Partial<ImageHotspotItem>) => {
        if (disabled) return;
        updateData({
            hotspots: data.hotspots.map((h) =>
                h.id === id ? { ...h, ...updates } : h
            ),
        });
    };

    const addHotspot = () => {
        if (disabled || data.hotspots.length >= MAX_HOTSPOTS) return;
        const newHotspot = createDefaultHotspot();
        const idx = data.hotspots.length;
        updateData({
            hotspots: [
                ...data.hotspots,
                {
                    ...newHotspot,
                    x: 30 + (idx % 4) * 20,
                    y: 25 + Math.floor(idx / 4) * 25,
                },
            ],
        });
    };

    const removeHotspot = (id: string) => {
        if (disabled) return;
        updateData({
            hotspots: data.hotspots.filter((h) => h.id !== id),
        });
    };

    const handleBackgroundChange = (content: BlockContent) => {
        updateData({ background: content });
    };

    const handleRemoveBackground = () => {
        updateData({ background: createDefaultBlockContent('image') });
    };

    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [changeBgDialogOpen, setChangeBgDialogOpen] = React.useState(false);
    const [contentDialogOpen, setContentDialogOpen] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);

    const selectedHotspot = selectedId
        ? data.hotspots.find((h) => h.id === selectedId)
        : undefined;

    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setSelectedId(null);
                setContentDialogOpen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleCanvasClick = React.useCallback(() => {
        setSelectedId(null);
    }, []);

    const bgSrc = getBackgroundImageSrc(data.background);

    return (
        <div className={cn('image-hotspot-edit space-y-6', className)}>
            <div className="space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <h3 className="font-semibold">{et.imageAndHotspots}</h3>
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setChangeBgDialogOpen(true)}
                            disabled={disabled}
                        >
                            <ImagePlus className="h-4 w-4 mr-1" />
                            {et.changeBackground}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleRemoveBackground}
                            disabled={disabled || !bgSrc}
                        >
                            <ImageMinus className="h-4 w-4 mr-1" />
                            {et.removeBackground}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addHotspot}
                            disabled={disabled || !bgSrc || data.hotspots.length >= MAX_HOTSPOTS}
                        >
                            <Plus className="h-4 w-4 mr-1" />
                            {et.addHotspot}
                        </Button>
                        {selectedHotspot && (
                            <>
                                <div className="flex items-center gap-2 border-l border-border pl-2">
                                    <Label className="text-sm shrink-0">{et.label}</Label>
                                    <Input
                                        value={selectedHotspot.label}
                                        onChange={(e) =>
                                            updateHotspot(selectedHotspot.id, {
                                                label: e.target.value,
                                            })
                                        }
                                        placeholder={et.hotspotLabelPlaceholder}
                                        disabled={disabled}
                                        className="w-32"
                                    />
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setContentDialogOpen(true)}
                                    disabled={disabled}
                                >
                                    {et.editContent}
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive"
                                    onClick={() => removeHotspot(selectedHotspot.id)}
                                    disabled={disabled}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {bgSrc ? (
                    <div
                        ref={containerRef}
                        className="relative cursor-default rounded-sm overflow-hidden border border-border"
                        onClick={handleCanvasClick}
                    >
                        <ImageDropCanvas
                            src={bgSrc}
                            alt="Background"
                            className="rounded-sm"
                            fitViewport
                        >
                            {data.hotspots.map((hotspot) => (
                                <DraggableHotspotNode
                                    key={hotspot.id}
                                    hotspot={hotspot}
                                    updateHotspot={updateHotspot}
                                    disabled={disabled}
                                    isSelected={selectedId === hotspot.id}
                                    onSelect={setSelectedId}
                                    containerRef={containerRef}
                                />
                            ))}
                        </ImageDropCanvas>
                    </div>
                ) : (
                    <div
                        className="rounded-sm border border-dashed border-border bg-muted/20 p-6 text-center text-muted-foreground cursor-pointer"
                        onClick={() => !disabled && setChangeBgDialogOpen(true)}
                    >
                        {et.uploadBackgroundFirst}
                    </div>
                )}
            </div>

            <Dialog open={changeBgDialogOpen} onOpenChange={setChangeBgDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{et.changeBackgroundImage}</DialogTitle>
                    </DialogHeader>
                    <div className="rounded-sm border border-dashed border-border bg-muted/20 p-4">
                        <BlockEditor
                            content={data.background}
                            onChange={handleBackgroundChange}
                            disabled={disabled}
                            compact={false}
                            allowedTypes={['image']}
                            imageConfig={{
                                hiddenFields: ['caption', 'size', 'alignment'],
                                defaults: { size: 'full', alignment: 'center' },
                                borderless: true,
                            }}
                        />
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={contentDialogOpen} onOpenChange={setContentDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{et.hotspotContent}: {selectedHotspot?.label || selectedHotspot?.id}</DialogTitle>
                    </DialogHeader>
                    {selectedHotspot && (
                        <BlockEditor
                            content={selectedHotspot.content}
                            onChange={(c) =>
                                updateHotspot(selectedHotspot.id, { content: c })
                            }
                            disabled={disabled}
                            compact={false}
                            allowedTypes={['text', 'image', 'rich-text']}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};
